import { Link } from "@tanstack/react-router";
import { PlusIcon, RefreshCwIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import type { AutomationListItem } from "./ForgeAutomationModel";
import { ForgeAutomationsList } from "./ForgeAutomationsList";
import { useForgeAutomationsIndex } from "./useForgeAutomationsIndex";

function buildAutomationItems(
  state: ReturnType<typeof useForgeAutomationsIndex>,
): readonly AutomationListItem[] {
  const cloudItems: readonly AutomationListItem[] = state.cloudAutomations
    .filter((automation) => automation.status !== "archived")
    .map((automation) => ({
      kind: "cloud",
      key: `cloud:${automation.id}`,
      automation,
      runs: [],
    }));
  const localItems: readonly AutomationListItem[] = state.localAutomations
    .filter((automation) => automation.status !== "archived")
    .map((automation) => ({
      kind: "local",
      key: `local:${automation.id}`,
      automation,
      runs: state.localRunsByAutomation[automation.id] ?? [],
    }));
  return [...cloudItems, ...localItems];
}

export function ForgeAutomationsPage() {
  const state = useForgeAutomationsIndex();
  const items = buildAutomationItems(state);
  const projectNames = new Map(
    state.projects.map((project) => [String(project.id), project.title]),
  );

  function toggle(item: AutomationListItem) {
    const status = item.automation.status === "active" ? "paused" : "active";
    if (item.kind === "cloud") {
      void state.updateCloudAutomation(item.automation.id, { status });
    } else {
      void state.updateLocalAutomation({ id: item.automation.id, status });
    }
  }

  function runNow(item: AutomationListItem) {
    if (item.kind === "cloud") {
      void state.runCloudAutomation(item.automation.id);
    } else {
      void state.runLocalAutomation(item.automation.id);
    }
  }

  return (
    <main className="min-h-0 flex-1 overflow-y-auto bg-background px-4 py-8 text-foreground sm:px-6 md:py-12">
      <div className="mx-auto w-full max-w-4xl">
        <header className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            {/* No title: the bar's crumb already says "Automations". */}
            <p className="text-sm text-muted-foreground">Reusable tasks that run for you.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              aria-label="Refresh automations"
              className={RESPONSIVE_ICON_BUTTON_CLASS}
              size="icon"
              variant="ghost"
              onClick={state.refresh}
            >
              <RefreshCwIcon />
            </Button>
            <Button
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              render={<Link to="/automations/new" />}
              size="sm"
            >
              <PlusIcon />
              Create
            </Button>
          </div>
        </header>

        <ForgeAutomationsList
          items={items}
          projectNames={projectNames}
          loading={state.loading}
          error={state.error}
          updatingAutomationKey={state.updatingAutomationKey}
          onRunNow={runNow}
          onToggle={toggle}
        />
      </div>
    </main>
  );
}
